import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { ArrowLeft, Plus, Save, Loader2, Trash2, Users, Building2 } from "lucide-react";

/**
 * Account Opening — mirrors Kinntegra-Old `client-account-creation` (Angular)
 *   Ref: /tmp/kinntegra-old/kinntegra-webapp/src/app/views/client-account-creation
 *
 * Structure per account tab:
 *   - Create Account For (BSE / MFU / P2P — BSE is the only live target)
 *   - Account Details (First / Second / Third Holder + Account Type)
 *   - Nominee toggle + up to 3 nominees (Name / Guardian / Relation / Share %)
 *   - Bank Details (Default Bank + multi Other Banks)
 *
 * Data source: family members already captured in the Introduction step.
 * Each family member becomes a candidate holder / nominee. Banks are stubbed
 * from a static list until BSE V2 KYC pull is wired up (see
 * /app/memory/BSE_V2_API_INTEGRATION_NOTES.md).
 */

const ACCOUNT_TYPES = ["Single", "Joint", "Anyone or Survivor", "Minor"];
const RELATIONS = ["Spouse", "Son", "Daughter", "Father", "Mother", "Brother", "Sister", "Guardian", "Other"];
const STUB_BANKS = [
  "HDFC Bank", "ICICI Bank", "State Bank of India", "Axis Bank", "Kotak Mahindra Bank",
  "Yes Bank", "IndusInd Bank", "IDFC First Bank", "Bank of Baroda", "Punjab National Bank",
];

const blankAccount = () => ({
  id: crypto.randomUUID?.() || String(Date.now() + Math.random()),
  create_bse: true, create_mfu: false, create_p2p: false,
  first_holder_id: "", second_holder_id: "", third_holder_id: "",
  account_type: "Single",
  has_nominee: false,
  nominees: [
    { name_id: "", guardian_id: "", relation: "", share: 0 },
    { name_id: "", guardian_id: "", relation: "", share: 0 },
    { name_id: "", guardian_id: "", relation: "", share: 0 },
  ],
  default_bank: "",
  other_banks: [],
  is_saved: false,
});

export default function LeadAccountOpeningPage() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([blankAccount()]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/lead-mgmt/${leadId}`);
        setLead(data.data);
        const saved = data.data?.account?.accounts;
        if (Array.isArray(saved) && saved.length) setAccounts(saved.map(a => ({ ...blankAccount(), ...a, is_saved: true })));
      } catch (e) {
        toast.error("Failed to load lead: " + (e?.response?.data?.detail || e.message));
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, [leadId]);

  const holders = useMemo(() => (lead?.introduction?.members || []).map(m => ({
    id: m.member_id || m.name, name: m.name, is_primary: !!m.is_primary,
  })), [lead]);

  const holderName = (id) => holders.find(h => h.id === id)?.name || "";

  const upd = (i, patch) => setAccounts(prev => prev.map((a, idx) => idx === i ? { ...a, ...patch } : a));
  const updNominee = (i, ni, patch) => setAccounts(prev => prev.map((a, idx) => {
    if (idx !== i) return a;
    const nominees = a.nominees.map((n, j) => j === ni ? { ...n, ...patch } : n);
    // Auto-balance shares across filled nominees so the total stays at 100
    const filled = nominees.filter(n => n.name_id).length;
    if (patch.share !== undefined && filled > 0) { /* explicit user edit; do nothing */ }
    return { ...a, nominees };
  }));

  const addAccount = () => { setAccounts(prev => [...prev, blankAccount()]); setActiveIdx(accounts.length); };
  const removeAccount = (i) => {
    if (accounts.length === 1) return toast.error("At least one account is required");
    setAccounts(prev => prev.filter((_, idx) => idx !== i));
    setActiveIdx(0);
  };

  const validate = () => {
    for (let i = 0; i < accounts.length; i++) {
      const a = accounts[i];
      if (!a.first_holder_id) return `Account ${i + 1}: First holder is required`;
      if (!a.account_type) return `Account ${i + 1}: Account type is required`;
      if (!a.default_bank) return `Account ${i + 1}: Default bank is required`;
      if (!a.create_bse && !a.create_mfu && !a.create_p2p) return `Account ${i + 1}: Select at least one platform`;
      if (a.has_nominee) {
        const filled = a.nominees.filter(n => n.name_id);
        if (filled.length === 0) return `Account ${i + 1}: Add at least one nominee`;
        const total = filled.reduce((s, n) => s + (Number(n.share) || 0), 0);
        if (total !== 100) return `Account ${i + 1}: Nominee share must total 100%`;
        for (const n of filled) if (!n.relation) return `Account ${i + 1}: Nominee relation is required`;
      }
    }
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) return toast.error(err);
    setBusy(true);
    try {
      await api.post(`/lead-mgmt/${lead.id}/account`, { accounts });
      toast.success("Account details saved");
      setAccounts(prev => prev.map(a => ({ ...a, is_saved: true })));
      navigate(`/leads/${leadId}/asset-allocation`);
    } catch (e) {
      toast.error("Save failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  if (loading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-teal-600" size={32}/></div>;
  if (!lead) return <div className="p-10 text-center text-gray-400">Lead not found.</div>;

  const active = accounts[activeIdx] || accounts[0];
  const showN2 = active.has_nominee && active.nominees[0]?.name_id && (Number(active.nominees[0]?.share) || 0) < 100;
  const showN3 = showN2 && active.nominees[1]?.name_id && ((Number(active.nominees[0]?.share) || 0) + (Number(active.nominees[1]?.share) || 0)) < 100;

  return (
    <div className="px-4 py-6" data-testid="lead-account-opening-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/leads/${leadId}/introduction`)}
            className="rounded-md border border-gray-300 bg-white px-2.5 py-1.5 hover:bg-gray-100"
            aria-label="Back"
            data-testid="acc-back-btn"
          ><ArrowLeft size={16}/></button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Account Opening</h1>
            <p className="text-xs text-gray-500 mt-0.5">Create BSE / MFU accounts for {lead.first_name} {lead.last_name}</p>
          </div>
        </div>
      </div>

      <Card className="border-gray-200 shadow-sm">
        <CardContent className="p-6">
          {/* Account tabs */}
          <div className="flex items-center gap-2 border-b mb-6 overflow-x-auto">
            {accounts.map((a, i) => (
              <button
                key={a.id}
                onClick={() => setActiveIdx(i)}
                className={`px-4 py-2 text-sm border-b-2 -mb-px whitespace-nowrap ${
                  i === activeIdx
                    ? "border-teal-600 text-teal-700 font-semibold"
                    : "border-transparent text-gray-500 hover:text-gray-800"
                }`}
                data-testid={`acc-tab-${i}`}
              >
                {i + 1} - {holderName(a.first_holder_id) || "New Account"}
              </button>
            ))}
            <button
              onClick={addAccount}
              className="ml-2 inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-dashed border-gray-300 text-xs text-gray-600 hover:bg-gray-50"
              data-testid="acc-add-btn"
            ><Plus size={12}/> Add Account</button>
          </div>

          {/* Create Account For */}
          <Section title="Create Account For">
            <div className="flex items-center gap-6">
              {[["create_bse","BSE"],["create_mfu","MFU"],["create_p2p","P2P"]].map(([k, label]) => (
                <label key={k} className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={!!active[k]}
                         onChange={(e) => upd(activeIdx, { [k]: e.target.checked })}
                         disabled={k !== "create_bse"}
                         data-testid={`acc-${k}`}
                         className="h-4 w-4 accent-teal-600 disabled:opacity-50"/>
                  {label} {k !== "create_bse" && <span className="text-[10px] text-gray-400">(coming soon)</span>}
                </label>
              ))}
            </div>
          </Section>

          {/* Account Details */}
          <Section title="Account Details">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <FieldSelect label="First Holder" value={active.first_holder_id}
                           onChange={v => upd(activeIdx, { first_holder_id: v, second_holder_id: "", third_holder_id: "" })}
                           options={holders.map(h => ({ value: h.id, label: h.name }))}
                           testid="acc-first-holder"/>
              <FieldSelect label="Second Holder" value={active.second_holder_id}
                           onChange={v => upd(activeIdx, { second_holder_id: v, third_holder_id: "" })}
                           options={holders.filter(h => h.id !== active.first_holder_id).map(h => ({ value: h.id, label: h.name }))}
                           disabled={!active.first_holder_id || active.account_type === "Single"}
                           testid="acc-second-holder"/>
              <FieldSelect label="Third Holder" value={active.third_holder_id}
                           onChange={v => upd(activeIdx, { third_holder_id: v })}
                           options={holders.filter(h => h.id !== active.first_holder_id && h.id !== active.second_holder_id).map(h => ({ value: h.id, label: h.name }))}
                           disabled={!active.second_holder_id || active.account_type === "Single"}
                           testid="acc-third-holder"/>
              <FieldSelect label="Account Type *" value={active.account_type}
                           onChange={v => upd(activeIdx, { account_type: v })}
                           options={ACCOUNT_TYPES.map(t => ({ value: t, label: t }))}
                           testid="acc-type"/>
            </div>
          </Section>

          {/* Nominee */}
          <Section title="Nominee">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 mb-3">
              <input type="checkbox" checked={active.has_nominee}
                     onChange={e => upd(activeIdx, { has_nominee: e.target.checked })}
                     data-testid="acc-has-nominee"
                     className="h-4 w-4 accent-teal-600"/>
              This account has a nominee
            </label>
            {active.has_nominee && (
              <div className="space-y-3">
                <NomineeRow idx={0} nominee={active.nominees[0]} onChange={p => updNominee(activeIdx, 0, p)} holders={holders} relations={RELATIONS} label="Nominee 1"/>
                {showN2 && <NomineeRow idx={1} nominee={active.nominees[1]} onChange={p => updNominee(activeIdx, 1, p)} holders={holders} relations={RELATIONS} label="Nominee 2"/>}
                {showN3 && <NomineeRow idx={2} nominee={active.nominees[2]} onChange={p => updNominee(activeIdx, 2, p)} holders={holders} relations={RELATIONS} label="Nominee 3"/>}
                <div className="text-xs text-gray-500">Total share: <span className="font-medium">{active.nominees.reduce((s, n) => s + (Number(n.share) || 0), 0)}%</span> (must equal 100)</div>
              </div>
            )}
          </Section>

          {/* Bank Details */}
          <Section title="Bank Details" icon={Building2}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FieldSelect label="Default Bank *" value={active.default_bank}
                           onChange={v => upd(activeIdx, { default_bank: v, other_banks: active.other_banks.filter(b => b !== v) })}
                           options={STUB_BANKS.map(b => ({ value: b, label: b }))}
                           testid="acc-default-bank"/>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5 block">Other Banks (Multi)</label>
                <div className="border border-gray-300 rounded-md p-2 max-h-40 overflow-y-auto space-y-1">
                  {STUB_BANKS.filter(b => b !== active.default_bank).map(b => (
                    <label key={b} className="flex items-center gap-2 text-sm px-2 py-1 rounded hover:bg-gray-50 cursor-pointer">
                      <input type="checkbox" checked={active.other_banks.includes(b)}
                             onChange={e => upd(activeIdx, {
                               other_banks: e.target.checked
                                 ? [...active.other_banks, b]
                                 : active.other_banks.filter(x => x !== b),
                             })}
                             className="h-4 w-4 accent-teal-600"/>
                      {b}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </Section>

          {/* Footer */}
          <div className="flex items-center justify-between pt-4 border-t mt-6">
            <button
              onClick={() => removeAccount(activeIdx)}
              disabled={accounts.length === 1}
              className="inline-flex items-center gap-1 text-rose-600 text-sm hover:bg-rose-50 rounded-md px-3 py-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="acc-remove-btn"
            ><Trash2 size={14}/> Remove This Account</button>
            <button
              onClick={save} disabled={busy}
              className="inline-flex items-center gap-2 rounded-md bg-[#1a7f6f] hover:bg-[#166a5c] text-white text-sm font-semibold px-8 py-2 disabled:opacity-60"
              data-testid="acc-save-btn"
            >
              {busy ? <Loader2 className="animate-spin" size={14}/> : <Save size={14}/>}
              Save & Next
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="mb-6">
      <h4 className="text-xs font-bold uppercase tracking-wider text-teal-700 mb-3 flex items-center gap-2">
        {Icon ? <Icon size={14}/> : <Users size={14}/>} {title}
      </h4>
      {children}
    </div>
  );
}

function FieldSelect({ label, value, onChange, options, disabled, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5 block">{label}</label>
      <select value={value || ""} onChange={e => onChange(e.target.value)} disabled={disabled}
              data-testid={testid}
              className="w-full h-9 px-3 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100 disabled:text-gray-400">
        <option value="">Select</option>
        {(options || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function NomineeRow({ idx, nominee, onChange, holders, relations, label }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 p-3 border border-gray-200 rounded-md bg-gray-50/60"
         data-testid={`acc-nominee-row-${idx}`}>
      <FieldSelect label={`${label} — Name`} value={nominee.name_id}
                   onChange={v => onChange({ name_id: v })}
                   options={holders.map(h => ({ value: h.id, label: h.name }))}
                   testid={`acc-nominee-name-${idx}`}/>
      <FieldSelect label="Guardian" value={nominee.guardian_id}
                   onChange={v => onChange({ guardian_id: v })}
                   options={holders.map(h => ({ value: h.id, label: h.name }))}
                   testid={`acc-nominee-guardian-${idx}`}/>
      <FieldSelect label="Relation" value={nominee.relation}
                   onChange={v => onChange({ relation: v })}
                   options={relations.map(r => ({ value: r, label: r }))}
                   testid={`acc-nominee-relation-${idx}`}/>
      <div>
        <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1.5 block">Share (%)</label>
        <input type="number" min="0" max="100" value={nominee.share ?? 0}
               onChange={e => onChange({ share: Math.max(0, Math.min(100, parseInt(e.target.value) || 0)) })}
               data-testid={`acc-nominee-share-${idx}`}
               className="w-full h-9 px-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"/>
      </div>
    </div>
  );
}
